import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Tag, Sparkles, Loader2, Info, Hash, Warehouse, Search } from "lucide-react";
import HsnTsnLookupModal from "../../dealer/HsnTsnLookupModal";
import api from "../../../lib/api";
import decodeVinModelYear from "../../../lib/decodeVinModelYear";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import SectionToggle from "../SectionToggle";
// import Zb1ExtendedFieldsSection from "./Zb1ExtendedFieldsSection"; // Feb-2026 Iter 19: nach PortalTransactionSection verschoben
import KbaLookupButton from "../KbaLookupButton";
import KiBadge from "../KiBadge";
import { StockStatusModal, STOCK_STATE_LABELS } from "../StockStatusModal";
import BrandLogo from "../../brand/BrandLogo";
import BrandCombobox from "../BrandCombobox";
import FrequentSelect from "../FrequentSelect";
import {
    MOTO_BRANDS, BIKE_BRANDS, BRAND_MODELS, EMISSION_CLASSES, CONDITION_OPTIONS,
    COLOR_BASES, FUEL_OPTIONS, DRIVE_TYPES, TRANSMISSION_OPTIONS, CYLINDER_OPTIONS,
    UMWELTPLAKETTEN, MOTORCYCLE_VEHICLE_TYPES, FEATURE_OPTIONS,
} from "../../../lib/listingConstants";
// Feb-2026 Iter 26: deriveLicenseClass + deriveAnnualTaxEUR moved to ExtraTechnicalSection

// Feb-2026 (User-Wunsch) — KBA-„Häufige Werks-Optionen" auf die Ausstattungs-
// Checkboxen (FEATURE_OPTIONS) mappen. Erkannte Optionen werden als Merkmal
// markiert; nicht zuordenbare Optionen bleiben (reduziert) im Bemerkungsfeld.
const KBA_OPTION_KEYWORDS = [
    { kw: ["kurven-abs", "kurvenabs", "schräglagen", "cornering abs"], feature: "Kurven-ABS" },
    { kw: ["abs"], feature: "ABS" },
    { kw: ["led tagfahr", "led-tagfahr", "tagfahrlicht"], feature: "LED Tagfahrlicht" },
    { kw: ["led"], feature: "LED Scheinwerfer" },
    { kw: ["xenon", "bi-xenon"], feature: "Xenonscheinwerfer" },
    { kw: ["laserlicht", "laser-licht"], feature: "Laserlicht" },
    { kw: ["usb"], feature: "USB" },
    { kw: ["wegfahrsperre", "immobilizer", "immobiliser"], feature: "Wegfahrsperre" },
    { kw: ["keyless", "schlüssellos", "fernentriegel", "fernbedien"], feature: "Keyless" },
    { kw: ["alarm"], feature: "Alarmanlage" },
    { kw: ["griffheiz", "heizgriff", "lenkerheiz"], feature: "Griffheizung" },
    { kw: ["navi"], feature: "Navigationssystem" },
    { kw: ["schaltautomat", "quickshifter", "quick-shifter", "blipper", "schaltassist"], feature: "Schaltautomat" },
    { kw: ["traktionskontrolle", "traction", "asr", "antriebsschlupf"], feature: "ASR" },
    { kw: ["totwinkel", "toter winkel", "blind spot", "blindspot"], feature: "Totwinkel-Assistent" },
    { kw: ["abstandswarner", "abstandsregel", "abstandstempomat", "radar"], feature: "Abstandswarner" },
    { kw: ["berganfahr", "hill hold", "hill-hold", "hanghalte"], feature: "Berganfahrassistent" },
    { kw: ["elektrostart", "e-start", "e-starter", "anlasser"], feature: "Elektrostarter" },
    { kw: ["kickstart"], feature: "Kickstarter" },
    { kw: ["katalysator", "kat-"], feature: "Katalysator" },
    { kw: ["windschild", "windschutzscheibe", "tourenscheibe", "scheibe"], feature: "Scheibe" },
    { kw: ["semi-aktiv", "semiaktiv", "elektronisches fahrwerk", "esa"], feature: "Semi-aktives Fahrwerk" },
];

function mapKbaOptionsToFeatures(options) {
    const matched = [];
    const remaining = [];
    (options || []).forEach((opt) => {
        const lower = String(opt).toLowerCase();
        const hit = KBA_OPTION_KEYWORDS.find((m) => m.kw.some((k) => lower.includes(k)));
        if (hit && FEATURE_OPTIONS.includes(hit.feature)) {
            if (!matched.includes(hit.feature)) matched.push(hit.feature);
        } else {
            remaining.push(opt);
        }
    });
    return { matched, remaining };
}

// Jul-2026 (User-Wunsch) — ausgelesenen Hersteller/Marke gegen die Marken-Dropdown-
// Liste matchen und den kanonischen Wert liefern (sonst null → keine Vorauswahl).
function matchBrandInList(name, list) {
    if (!name || !Array.isArray(list)) return null;
    const n = String(name).trim().toLowerCase();
    if (!n) return null;
    let hit = list.find((b) => b.toLowerCase() === n);
    if (hit) return hit;
    hit = list.find((b) => n.includes(b.toLowerCase()) || b.toLowerCase().includes(n));
    return hit || null;
}

/**
 * BasicsSection — Fahrzeug-Stammdaten (Phase 4.6 Extraktion aus CreateListingPage).
 *
 * Enthält Titel, Marke, Modell, Jahr, Kilometer, Farbe, Preise (B2C/B2B),
 * MwSt-Optionen, Fahrzeugtyp, Hubraum / Leistung, Lager / Schlüssel /
 * Papiere und alle weiteren Pflicht- und Pflicht-Optional-Felder.
 *
 * Pure presentational — alle Setter und Flags kommen via Props vom Parent
 * (`CreateListingPage`). Test-IDs wurden 1:1 aus dem Original übernommen.
 */
export default function BasicsSection({ form, setForm, update, markAiFields, user, isMoto, isAuction, isDealer, essentialOnly = false, orderMode = false, open: openProp, onOpenChange }) {
    const { t } = useTranslation();
    // Jul-2026 (User-Wunsch) — Modelljahr aus 10. FIN-Stelle (unverbindlich).
    const vinModelYear = useMemo(() => decodeVinModelYear(form.vin), [form.vin]);

    // Jul-2026 (User-Wunsch): KBA-Bezeichnung (Hersteller + Handelsbezeichnung)
    // live vor den HSN/TSN-Feldern anzeigen, sobald beide erfasst sind.
    const [kbaInfo, setKbaInfo] = useState(null);
    // Jul-2026 (User-Wunsch) — HSN/TSN-Nachschlagen für ALLE registrierten Nutzer.
    const [hsnLookupOpen, setHsnLookupOpen] = useState(false);
    useEffect(() => {
        const hsnOk = (form.hsn || "").length === 4;
        const tsnOk = (form.tsn || "").length >= 3;
        if (!hsnOk || !tsnOk) { setKbaInfo(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/hsn-tsn/lookup?hsn=${encodeURIComponent(form.hsn)}&tsn=${encodeURIComponent(form.tsn)}`);
                setKbaInfo(data?.found ? data.result : { not_found: true });
            } catch { setKbaInfo(null); }
        }, 500);
        return () => clearTimeout(t);
    }, [form.hsn, form.tsn]);

    // Jul-2026 (User-Wunsch): Euro-Norm aus Erstzulassung/Baujahr vorschlagen,
    // wenn keine Schadstoffklasse erfasst ist (Stichtage Neuzulassungen EU).
    const euroSuggestion = useMemo(() => {
        const y = form.first_registration_date
            ? parseInt(form.first_registration_date.slice(0, 4), 10)
            : (form.year ? Number(form.year) : null);
        if (!y || Number.isNaN(y)) return null;
        if (y >= 2025) return "Euro 5+";
        if (y >= 2021) return "Euro 5";
        if (y >= 2017) return "Euro 4";
        if (y >= 2007) return "Euro 3";
        if (y >= 2004) return "Euro 2";
        return null;
    }, [form.first_registration_date, form.year]);
    // Jul-2026 (User-Wunsch) — Auto-Vervollständigung aus der Fahrzeug-
    // Wissensdatenbank: FIN-Präfix → Hersteller/Modell/Baujahr vorausfüllen
    // (nur leere Felder; bleibt manuell korrigierbar).
    const [vkHint, setVkHint] = useState(null);
    useEffect(() => {
        const vin = (form.vin || "").replace(/[^A-Za-z0-9]/g, "");
        if (vin.length < 10) { setVkHint(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/vehicle-knowledge/lookup", { params: { vin } });
                if (!r.data.found) { setVkHint(null); return; }
                const it = r.data.item;
                setVkHint(it);
                const filled = [];
                setForm((p) => {
                    const n = { ...p };
                    if (!p.brand && it.manufacturer) { n.brand = it.manufacturer; filled.push("Hersteller"); }
                    if (!p.model && it.model) { n.model = it.model; filled.push("Modell"); }
                    if (!p.year && it.model_year) { n.year = it.model_year; filled.push("Baujahr"); }
                    return filled.length ? n : p;
                });
                if (filled.length) {
                    toast.info(`Aus Wissensdatenbank vorausgefüllt: ${filled.join(", ")}${it.verified ? "" : " (KI-gelernt — bitte prüfen)"} — manuell korrigierbar.`);
                }
            } catch { /* Lookup optional */ }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.vin]);
    // Feb-2026 Iter 27: priceAi/fetchPriceSuggestion wurden nach PriceFieldsBlock extrahiert.
    const [specAi, setSpecAi] = useState({ loading: false, applied: 0, items: [] });
    // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v9) — Header-„KI"-Button:
    // stößt alle für Fahrzeug-Stammdaten relevanten KI-Abrufe parallel an.
    const [kbaAiLoading, setKbaAiLoading] = useState(false);
    // Juni-2026 (User-Wunsch) — Lager-Status-Popup für Neufahrzeuge.
    const [stockModalOpen, setStockModalOpen] = useState(false);
    // Aug-2026 (User-Wunsch) — fehlende E-Antrieb-Daten per KI im Netz recherchieren.
    const [eResearching, setEResearching] = useState(false);
    const researchElectric = async () => {
        setEResearching(true);
        try {
            const r = await api.post("/listings/ai-electric-specs", {
                brand: form.brand, model: form.model, year: form.year || null,
            });
            const d = r.data || {};
            const updates = {};
            const filled = [];
            if (d.power_kw != null && !form.power_kw) { updates.power_kw = String(d.power_kw); filled.push("Leistung"); }
            if (d.range_km != null && !form.electric_range_km && !form.range_km) {
                updates.electric_range_km = String(d.range_km); updates.range_km = String(d.range_km); filled.push("Reichweite");
            }
            if (d.charging_time_hours != null && !form.charging_time_hours) { updates.charging_time_hours = String(d.charging_time_hours); filled.push("Ladedauer"); }
            if (d.battery_type && !form.electric_battery_type) {
                updates.electric_battery_type = d.battery_type === "removable" ? "herausnehmbar" : "fest";
                updates.battery_type = d.battery_type; filled.push("Akku-Bauart");
            }
            if (filled.length) {
                setForm((p) => ({ ...p, ...updates }));
                toast.success(`KI-Recherche: ${filled.join(", ")} ergänzt${d.web_used ? " (Web-Recherche)" : ""}.`);
            } else {
                toast.info(d.note || "Keine fehlenden Daten ergänzt — Felder sind bereits gefüllt oder es wurden keine verlässlichen Werte gefunden.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Recherche fehlgeschlagen.");
        } finally {
            setEResearching(false);
        }
    };
    const handleConditionChange = (v) => {
        update("condition", v);
        if (v === "Neufahrzeug") {
            const cleared = [];
            if (form.first_registration_date) { update("first_registration_date", ""); cleared.push("Erstzulassung"); }
            if (form.mileage && Number(form.mileage) > 0) { update("mileage", 0); cleared.push("KM-Stand"); }
            if (form.previous_owners && Number(form.previous_owners) > 0) { update("previous_owners", 0); cleared.push("Vorbesitzer"); }
            if (cleared.length) {
                toast.info(`Neufahrzeug: ${cleared.join(", ")} zurückgesetzt (bei Neufahrzeugen nicht relevant).`);
            }
            setStockModalOpen(true);
        }
    };
    const basicsAiBusy = specAi.loading || kbaAiLoading;
    const runAllBasicsAi = async () => {
        const tasks = [];
        const hasBrandModel = !!(form.brand && form.model);
        const hasHsnTsn = !!((form.hsn || "").length === 4 && (form.tsn || "").length >= 3 && (form.tsn || "").length <= 12);
        if (!hasBrandModel && !hasHsnTsn) {
            toast.info("Bitte mind. Marke + Modell ODER HSN + TSN ausfüllen — dann die KI starten.");
            return;
        }
        if (hasBrandModel) {
            tasks.push(fetchSpecLookup());
        }
        if (hasHsnTsn) {
            // KBA-Lookup parallel anstoßen — Ergebnis schreibt in
            // form-Felder, sofern Backend bestätigt und kein Mismatch vorliegt.
            tasks.push((async () => {
                setKbaAiLoading(true);
                try {
                    const { data } = await api.post("/vehicles/kba-lookup", {
                        hsn: form.hsn,
                        tsn: form.tsn,
                        first_registration_date: form.first_registration_date || null,
                        expected_brand: (form.brand || "").trim() || null,
                        expected_model: (form.model || "").trim() || null,
                    });
                    if (!data?.ok) {
                        if (data?.error === "brand_mismatch") {
                            toast.error(data.warning || "KBA-Lookup: Marken-Mismatch", { duration: 6000 });
                        } else if (data?.error === "low_confidence") {
                            toast.warning(data.warning || "KBA-Lookup: niedrige Konfidenz", { duration: 5000 });
                        }
                        return;
                    }
                    const d = data.data || {};
                    const normBrand = d.brand
                        ? d.brand.charAt(0).toUpperCase() + d.brand.slice(1).toLowerCase()
                        : null;
                    // Nur LEERE Felder befüllen — nie User-Eingabe überschreiben.
                    let kbaApplied = 0;
                    const tryFill = (key, val) => {
                        if (val == null || val === "") return;
                        const cur = form[key];
                        if (cur === undefined || cur === null || cur === "") {
                            update(key, typeof val === "number" ? String(val) : val);
                            kbaApplied += 1;
                        }
                    };
                    tryFill("brand", matchBrandInList(normBrand, isMoto ? MOTO_BRANDS : BIKE_BRANDS) || matchBrandInList(d.manufacturer, isMoto ? MOTO_BRANDS : BIKE_BRANDS));
                    tryFill("manufacturer", d.manufacturer);
                    tryFill("vehicle_class", d.vehicle_class);
                    tryFill("displacement", d.displacement);
                    tryFill("power_kw", d.power_kw);
                    tryFill("power_hp", d.power_hp);
                    tryFill("max_speed_kmh", d.top_speed_kmh);
                    tryFill("emission_class", d.emission_class);
                    tryFill("fuel", d.fuel);
                    if (kbaApplied > 0) {
                        toast.success(`KBA-Daten: ${kbaApplied} leere Felder aus HSN/TSN ergänzt.`);
                    }
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "KBA-Lookup fehlgeschlagen");
                } finally {
                    setKbaAiLoading(false);
                }
            })());
        }
        await Promise.allSettled(tasks);
    };
    // Helper: alle Pflichtfelder bekommen einen einheitlichen vollen roten Rahmen
    // (User-Wunsch 21 May 2026, Iteration 3: jetzt OPTISCH IDENTISCH egal ob gefüllt
    // oder leer — gleicher 2-px-roter Rahmen für alle Pflichtfelder. Kein blasses
    // border-red-300 / starker red-ring-Mix mehr).
    // Feb-2026 (User-Wunsch UX): reqCls liefert jetzt zusätzlich ein title-Attribut
    // damit jeder rot umrahmte Inserent beim Hover lesen kann, dass es ein Pflichtfeld ist.
    const reqCls = () => "border-2 border-red-400";
    const reqTitle = "Pflichtfeld — wird beim Speichern geprüft";

    // Feb-2026 (User-Wunsch 22 May): Vermittlungsprovision = Preis B2C − Preis B2B.
    // Sobald beide Preise eingegeben sind, wird die Differenz automatisch als
    // Provision in das Vermittlungsprovision-Feld eingetragen. Inserent kann
    // den Wert anschließend manuell überschreiben.
    useEffect(() => {
        if (!isDealer || isAuction) return;
        const b2c = parseFloat(form.price);
        const b2b = parseFloat(form.price_b2b);
        if (!isFinite(b2c) || !isFinite(b2b)) return;
        const diff = Math.max(0, Math.round(b2c - b2b));
        if (String(diff) !== String(form.referral_commission_eur ?? "")) {
            update("referral_commission_eur", String(diff));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.price, form.price_b2b, isDealer, isAuction]);

    const fetchSpecLookup = async () => {
        if (!form.brand || !form.model) {
            toast.info("Bitte Marke + Modell ausfüllen.");
            return;
        }
        setSpecAi({ loading: true, applied: 0, items: [] });
        try {
            const year = form.first_registration_date
                ? parseInt(form.first_registration_date.slice(0, 4), 10)
                : (form.year ? Number(form.year) : null);
            const { data } = await api.post("/listings/ai-spec-lookup", {
                brand: form.brand,
                model: form.model,
                year,
                category: form.category || "motorcycle",
            });
            if (data.mocked) {
                toast.warning("KI nicht erreichbar — Werks-Lookup übersprungen.");
                setSpecAi({ loading: false, applied: 0, items: [] });
                return;
            }
            const specs = data.specs || {};
            // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v6): Übernommene
            // Felder mit Name + Wert sichtbar machen, statt nur „1 Werks-Feld".
            const LABEL = {
                displacement: { label: "Hubraum", unit: " cm³" },
                power_kw: { label: "Leistung", unit: " kW" },
                power_hp: { label: "Leistung", unit: " PS" },
                max_speed_kmh: { label: "Vmax", unit: " km/h" },
                top_speed_kmh: { label: "Vmax", unit: " km/h" },
                fuel: { label: "Kraftstoff", unit: "" },
                emission_class: { label: "Schadstoff-Klasse", unit: "" },
                vehicle_class: { label: "Fahrzeug-Klasse", unit: "" },
                vehicle_type: { label: "Fahrzeug-Typ", unit: "" },
                number_of_seats: { label: "Sitzplätze", unit: "" },
                seat_height_mm: { label: "Sitzhöhe", unit: " mm" },
                weight_empty_kg: { label: "Leergewicht", unit: " kg" },
                max_payload_kg: { label: "Zuladung", unit: " kg" },
                tank_capacity_liters: { label: "Tankvolumen", unit: " l" },
                typical_consumption: { label: "Verbrauch", unit: "" },
                manufacturer: { label: "Hersteller", unit: "" },
                cylinder_count: { label: "Zylinder", unit: "" },
                transmission: { label: "Getriebe", unit: "" },
                drive_type: { label: "Antriebsart", unit: "" },
                color_base: { label: "Farbe", unit: "" },
                engine_type: { label: "Motor-Typ", unit: "" },
                gears: { label: "Gänge", unit: "" },
                tank_capacity_l: { label: "Tankvolumen", unit: " l" },
                tire_size_front: { label: "Reifen vorne (15.1)", unit: "" },
                tire_size_rear: { label: "Reifen hinten (15.2)", unit: "" },
                battery_range: { label: "Reichweite", unit: " km" },
                battery_capacity_kwh: { label: "Batterie", unit: " kWh" },
            };
            const items = [];
            Object.entries(specs).forEach(([k, v]) => {
                if (form[k] !== undefined && form[k] !== null && form[k] !== "") return;
                update(k, typeof v === "number" ? String(v) : v);
                const meta = LABEL[k] || { label: k, unit: "" };
                items.push({ key: k, label: meta.label, value: `${v}${meta.unit}` });
            });
            setSpecAi({ loading: false, applied: items.length, items });
            if (items.length > 0) {
                const preview = items.slice(0, 3).map((it) => `${it.label}: ${it.value}`).join(" · ");
                const tail = items.length > 3 ? ` (+${items.length - 3} weitere)` : "";
                toast.success(
                    `${items.length} Werks-Daten übernommen (${Math.round(data.confidence * 100)} % Vertrauen): ${preview}${tail}`,
                    { duration: 6000 }
                );
            } else {
                toast.info("Alle Werks-Felder waren schon ausgefüllt.");
            }
        } catch (err) {
            setSpecAi({ loading: false, applied: 0, items: [] });
            toast.error(err?.response?.data?.detail || "Werks-Lookup fehlgeschlagen");
        }
    };


    return (
        <>
            {/* Basics */}
            {/* Phase 3.80 — Fahrzeug-Stammdaten in einem aufklappbaren Akkordeon (defaultOpen). */}
            <SectionToggle
                title="Fahrzeug-Stammdaten"
                titleExtra={form.title || [form.brand, form.model].filter(Boolean).join(" ")}
                icon={Tag}
                defaultOpen
                open={openProp}
                onOpenChange={onOpenChange}
                testId="basics-section"
                headerExtras={(
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); runAllBasicsAi(); }}
                        disabled={basicsAiBusy}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] disabled:opacity-50"
                        data-testid="basics-ai-refresh-btn"
                        title="Stößt alle KI-Abrufe in Fahrzeug-Stammdaten an: Werks-Daten (Marke+Modell) und KBA-Daten (HSN+TSN+EZ)."
                        aria-label="Alle KI in Fahrzeug-Stammdaten aktualisieren"
                    >
                        {basicsAiBusy
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                        <span>KI</span>
                    </button>
                )}
            >
            <div className="tw-fz-stammdaten bg-white border border-gray-200 rounded-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Feb-2026 (User-Wunsch Iter 28) — Titel-Feld (mit "Vorschlag"-Button)
                    wurde in `PriceFieldsBlock` verschoben und sitzt jetzt direkt ÜBER
                    dem Quality-Score-Card im Preise-Block (User-Flow: erst Titel
                    setzen, dann Quality-Tipp sehen, dann MwSt + Preise wählen). */}
                {/* Feb-2026 (User-Wunsch): Hersteller (2) + Marke * + Modell *
                    in einer 3-Spalten-Zeile. Hersteller wurde aus dem
                    ZB1-erweitert-Block hochgezogen, da er bei der Erst-
                    eingabe nützlicher zwischen Marke direkt sichtbar ist. */}
                <div className={`md:col-span-2 grid grid-cols-1 ${essentialOnly ? "md:grid-cols-2" : "md:grid-cols-3"} gap-4`} data-testid="manufacturer-brand-model-row">
                {!essentialOnly && (
                <div>
                    <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Hersteller (2) <KiBadge field="manufacturer" /></Label>
                        <KbaLookupButton
                            label="KBA Daten"
                            hsn={form.hsn}
                            tsn={form.tsn}
                            firstRegistrationDate={form.first_registration_date}
                            expectedBrand={form.brand}
                            expectedModel={form.model}
                            currentDisplacement={form.displacement}
                            currentPowerKw={form.power_kw}
                            onApply={(mapped, options) => {
                                const appliedKeys = [];
                                // Jul-2026 (User-Wunsch) — Hersteller als Marke-Vorauswahl,
                                // sofern er im Marken-Dropdown vorhanden ist (sonst Marke leer lassen).
                                if (!form.brand) {
                                    const brandList = isMoto ? MOTO_BRANDS : BIKE_BRANDS;
                                    const canonical = matchBrandInList(mapped.brand, brandList) || matchBrandInList(mapped.manufacturer, brandList);
                                    if (canonical) mapped = { ...mapped, brand: canonical };
                                    else delete mapped.brand; // kein Dropdown-Treffer → Marke nicht setzen
                                }
                                Object.entries(mapped).forEach(([k, v]) => {
                                    if (v != null && v !== "") {
                                        update(k, v);
                                        appliedKeys.push(k);
                                    }
                                });
                                // Feb-2026 (User-Wunsch) — KBA-Werks-Optionen primär als
                                // Ausstattungs-Merkmale markieren statt als Fließtext.
                                if (options && options.length) {
                                    const { matched, remaining } = mapKbaOptionsToFeatures(options);
                                    if (matched.length) {
                                        const cur = Array.isArray(form.features) ? form.features : [];
                                        const mergedFeatures = [...new Set([...cur, ...matched])];
                                        update("features", mergedFeatures);
                                        appliedKeys.push("features");
                                    }
                                    // Nur nicht zuordenbare Optionen (reduziert) ins Bemerkungsfeld
                                    if (remaining.length && !form.bemerkungen) {
                                        update("bemerkungen", `Weitere Werks-Optionen (KBA): ${remaining.join(", ")}`);
                                        appliedKeys.push("bemerkungen");
                                    }
                                }
                                if (typeof markAiFields === "function") markAiFields(appliedKeys);
                            }}
                        />
                    </div>
                    <Input
                        value={form.manufacturer ?? ""}
                        onChange={(e) => update("manufacturer", e.target.value)}
                        placeholder="z. B. PIAGGIO"
                        className="mt-1.5 rounded-sm"
                        data-testid="form-manufacturer"
                    />
                </div>
                )}
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">{t("create_listing.brand")} <KiBadge field="brand" /></Label>
                    <div className="mt-1.5">
                        <BrandCombobox
                            value={form.brand}
                            onValueChange={(v) => update("brand", v)}
                            placeholder={t("create_listing.brand")}
                            searchPlaceholder="Marke suchen oder eingeben…"
                            testId="form-brand"
                            triggerClassName={reqCls("brand")}
                            groups={(() => {
                                const all = isMoto ? MOTO_BRANDS : BIKE_BRANDS;
                                const contracted = (user?.dealer_authorized_brands || []).filter((b) => all.includes(b));
                                const tops = (user?.favorite_brands || []).filter((b) => all.includes(b) && !contracted.includes(b)).slice(0, 5);
                                const rest = all.filter((b) => !contracted.includes(b) && !tops.includes(b));
                                const node = (b) => <span className="inline-flex items-center gap-2"><BrandLogo brand={b} size={20} decorative /> {b}</span>;
                                const g = [];
                                if (contracted.length > 0) g.push({ heading: "★ Vertragshändler-Marken", items: contracted.map((b) => ({ value: b, node: node(b) })) });
                                if (tops.length > 0) g.push({ heading: "⭐ Ihre Top-Marken", items: tops.map((b) => ({ value: b, node: node(b) })) });
                                g.push({ heading: (contracted.length > 0 || tops.length > 0) ? "Alle Marken" : undefined, items: rest.map((b) => ({ value: b, node: node(b) })) });
                                return g;
                            })()}
                        />
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Modell (D.3) <KiBadge field="model" /></Label>
                        <button
                            type="button"
                            onClick={fetchSpecLookup}
                            disabled={specAi.loading || !form.brand || !form.model}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-100 hover:bg-gray-200 text-[#111827] border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            data-testid="form-spec-lookup-btn"
                            title="KI lädt typische Werks-Spezifikationen (Hubraum, Leistung, Antrieb …) für dieses Modell."
                        >
                            {specAi.loading
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Lädt…</>
                                : <><Sparkles className="w-3 h-3" /> Werks-Daten</>}
                        </button>
                    </div>
                    <Input
                        value={form.model}
                        onChange={(e) => update("model", e.target.value)}
                        required
                        className={`mt-1.5 rounded-sm ${reqCls("model")}`}
                        data-testid="form-model"
                        list={`models-${form.brand}`}
                        placeholder={(BRAND_MODELS[form.brand] || []).length ? "Modell wählen oder eingeben…" : ""}
                    />
                    {(BRAND_MODELS[form.brand] || []).length > 0 && (
                        <datalist id={`models-${form.brand}`}>
                            {BRAND_MODELS[form.brand].map((m) => <option key={m} value={m} />)}
                        </datalist>
                    )}
                    {specAi.applied > 0 && (
                        <div className="mt-1 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1.5" data-testid="form-spec-lookup-applied">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-violet-800 mb-0.5">
                                ✓ {specAi.applied} Werks-{specAi.applied === 1 ? "Feld" : "Felder"} übernommen
                            </p>
                            <ul className="text-[11px] text-gray-800 space-y-0.5">
                                {specAi.items.map((it) => (
                                    <li key={it.key} className="flex justify-between gap-2">
                                        <span className="text-gray-500">{it.label}:</span>
                                        <span className="font-bold font-mono-num text-right truncate" title={String(it.value)}>{it.value}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                onClick={() => setSpecAi({ loading: false, applied: 0, items: [] })}
                                className="mt-1 text-[10px] text-gray-500 hover:text-gray-900 underline"
                                data-testid="form-spec-lookup-dismiss"
                            >
                                Ausblenden
                            </button>
                        </div>
                    )}
                </div>
                </div>
                {/* Feb-2026 (User-Wunsch 24 May Iteration 8) — Fahrzeugtyp, Zustand,
                    Anzahl der Sitze und Sitzhöhe in einer 4-Spalten-Zeile mit
                    einheitlichem Label-Stil und gleicher Eingabehöhe. */}
                <div className={`md:col-span-2 grid grid-cols-1 sm:grid-cols-2 ${essentialOnly ? "md:grid-cols-2" : "md:grid-cols-4"} gap-3 items-end`} data-testid="type-condition-seats-row">
                    {isMoto && (
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrzeugtyp</Label>
                            <FrequentSelect
                                storageKey="listing.vehicle_type"
                                value={form.vehicle_type || "any"}
                                onValueChange={(v) => update("vehicle_type", v === "any" ? "" : v)}
                                options={[{ value: "any", label: "– bitte wählen –" }, ...MOTORCYCLE_VEHICLE_TYPES.map((vt) => ({ value: vt, label: vt }))]}
                                noFreqValues={["any"]}
                                placeholder="– bitte wählen –"
                                triggerClassName={`mt-1.5 rounded-sm ${reqCls("vehicle_type")}`}
                                testId="form-vehicle-type"
                            />
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5 flex-wrap">
                            {t("create_listing.condition_label")} <KiBadge field="condition" />
                            {/* Juni-2026 (User-Wunsch) — Lager-Status („Bestand"/„Vorlauf"/„Bestell")
                                direkt neben dem Wort „Zustand" (Muster wie „Hersteller (2)"),
                                nur bei Neufahrzeug. Klick öffnet das Lager-Status-Popup. */}
                            {form.condition === "Neufahrzeug" && (
                                <button
                                    type="button"
                                    onClick={() => setStockModalOpen(true)}
                                    className={`inline-flex items-center gap-1 normal-case tracking-normal text-[10px] font-bold rounded-sm border px-1.5 py-0.5 transition-colors ${
                                        form.stock_state
                                            ? "border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100"
                                            : "border-dashed border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-700"
                                    }`}
                                    data-testid="condition-stock-state-badge"
                                    title="Lager-Status festlegen"
                                >
                                    <Warehouse className="w-3 h-3" />
                                    {form.stock_state
                                        ? <>{STOCK_STATE_LABELS[form.stock_state]}{form.stock_count ? ` · ${form.stock_count} Stk.` : ""}</>
                                        : <>Lager-Status</>}
                                </button>
                            )}
                        </Label>
                        <FrequentSelect
                            storageKey="listing.condition"
                            value={form.condition}
                            onValueChange={handleConditionChange}
                            options={CONDITION_OPTIONS}
                            triggerClassName={`mt-1.5 rounded-sm ${reqCls("condition")}`}
                            testId="listing-condition"
                        />
                    </div>
                    <StockStatusModal open={stockModalOpen} onOpenChange={setStockModalOpen} form={form} update={update} />
                    {/* Feb-2026 (User-Wunsch) — „Nur Pflichtfelder": optionale Felder
                        (Anzahl Sitze, Sitzhöhe) ohne roten Pflichtfeld-Rahmen ausblenden. */}
                    {!essentialOnly && (
                    <>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Anzahl der Sitze (S.1) <KiBadge field="seat_count" /></Label>
                        <Select value={form.seat_count || "2"} onValueChange={(v) => update("seat_count", v)}>
                            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="form-seat-count"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">1</SelectItem>
                                <SelectItem value="2">2</SelectItem>
                                <SelectItem value="3">3</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5" htmlFor="seat-height-input">
                            Sitzhöhe
                            <KiBadge field="seat_height_mm" />
                        </Label>
                        <div className="relative mt-1.5">
                            <Input
                                id="seat-height-input"
                                type="number"
                                min="0"
                                max="1200"
                                value={form.seat_height_mm || ""}
                                onChange={(e) => update("seat_height_mm", e.target.value)}
                                placeholder="z. B. 820"
                                className="rounded-sm font-mono-num pr-10"
                                data-testid="form-seat-height"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px] pointer-events-none">mm</span>
                        </div>
                    </div>
                    </>
                    )}
                </div>
                {/* Mai-2026 — Preis B2C und KI-Preis-Button wurden ans Ende der Sektion verschoben (User-Wunsch), siehe Block unter MwSt + B2B. */}
                {/* Phase 3.91 — B2B-Preis: Mai-2026 verschoben unter MwSt-Block (User-Wunsch) */}
                {/* Phase 3.78/3.91 — Vermittlungsprovision: Mai-2026 verschoben unter MwSt-Block (siehe weiter unten) */}
                {/* Phase 4.9 — KI-Preisvorschlag Result Card — Feb-2026: ans Ende
                    unter die Preis-Felder verschoben (User-Wunsch 22 May). Siehe Block weiter unten. */}
                {/* Phase 3.91 — Unfall/Schaden-Block ans untere Ende der Fahrzeug-Stammdaten verschoben */}
                {/* Phase 3.86 — Lagerbestand wurde in die Disposition-Sektion verschoben */}
                {/* Mai-2026 (User-Wunsch 21 May Iteration 3): Erstzulassung + Anzahl Vorbesitzer + Kilometerstand in einer Zeile */}
                {!orderMode && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="registration-row">
                    {!isMoto && (
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">{t("create_listing.year")} * <KiBadge field="year" /></Label>
                            <Input type="number" value={form.year} onChange={(e) => update("year", e.target.value)} required className={`mt-1.5 rounded-sm ${reqCls("first_registration_date")}`} data-testid="form-year" />
                        </div>
                    )}
                    {isMoto && (
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Erstzulassung (B) <KiBadge field="first_registration_date" /></Label>
                            <Input
                                type="date"
                                value={form.first_registration_date}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm((p) => ({
                                        ...p,
                                        first_registration_date: v,
                                        year: v ? parseInt(v.slice(0, 4), 10) : p.year,
                                    }));
                                }}
                                max={new Date().toISOString().slice(0, 10)}
                                required
                                className={`mt-1.5 rounded-sm ${reqCls("first_registration_date")}`}
                                data-testid="form-erstzulassung"
                            />
                        </div>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Modelljahr laut FahrgestellNr. (10. Stelle der FIN),
                        unverbindlich (ISO 3779, 30-Jahres-Zyklus). Zwischen Erstzulassung und Vorbesitzer. */}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5" title="Aus der 10. Stelle der Fahrgestellnummer abgeleitet (unverbindlich, ISO 3779).">
                            Modelljahr lt. FIN (nicht Herstellungsjahr)
                        </Label>
                        <Input
                            readOnly
                            value={vinModelYear.year ? `${vinModelYear.year}` : ""}
                            placeholder={form.vin ? "FIN unvollständig" : "FIN erfassen"}
                            title={vinModelYear.year ? `Aus 10. FIN-Stelle „${vinModelYear.char}" — unverbindlich, ggf. abweichend vom Baujahr.` : "Vollständige FIN (17 Zeichen) erforderlich."}
                            className="mt-1.5 rounded-sm font-mono-num bg-gray-50 text-gray-700"
                            data-testid="form-vin-model-year"
                        />
                    </div>
                    {vkHint && (
                        <div className="col-span-full text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-sm px-2.5 py-1.5" data-testid="vk-autofill-hint">
                            Wissensdatenbank ({vkHint.vin_prefix}): {[vkHint.manufacturer, vkHint.model, vkHint.model_year].filter(Boolean).join(" · ") || "—"}
                            {vkHint.equipment?.length ? ` · Ausstattung: ${vkHint.equipment.slice(0, 4).join(", ")}` : ""}
                            {vkHint.verified ? " · ✓ verifiziert" : " · KI-gelernte Daten können fehlerhaft sein — bitte prüfen"}
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Anzahl Vorbesitzer <KiBadge field="previous_owners" /></Label>
                        <Input type="number" min="0" required value={form.previous_owners} onChange={(e) => update("previous_owners", e.target.value)} className={`mt-1.5 rounded-sm font-mono-num ${reqCls("previous_owners")}`} data-testid="extra-previous-owners" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">{t("create_listing.mileage")} <KiBadge field="mileage" /></Label>
                        {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v11): Vor-eingestellte 0
                            wird als Placeholder angezeigt — sobald der User tippt, ist das
                            Feld leer und die erste Ziffer überschreibt nichts. */}
                        <Input
                            type="number"
                            required
                            value={(form.mileage === 0 || form.mileage === "0") ? "" : form.mileage}
                            onChange={(e) => update("mileage", e.target.value)}
                            placeholder="0"
                            className={`mt-1.5 rounded-sm ${reqCls("mileage")}`}
                            data-testid="form-mileage"
                        />
                    </div>
                </div>
                )}
                {/* Mai-2026 (User-Wunsch 21 May Iteration 4) — Farbbasis + Farbe Hersteller + Finish in dieser Reihenfolge in einer Zeile.
                    Anzahl der Sitze wurde nach oben in die Fahrzeugtyp/Zustand-Reihe verschoben. */}
                <div className={`md:col-span-2 grid grid-cols-1 ${essentialOnly ? "md:grid-cols-1" : "md:grid-cols-3"} gap-4`} data-testid="color-row">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Farbbasis</Label>
                        <FrequentSelect
                            storageKey="listing.color_base"
                            value={form.color_base}
                            onValueChange={(v) => update("color_base", v)}
                            options={COLOR_BASES}
                            placeholder="Farbe wählen…"
                            triggerClassName={`mt-1.5 rounded-sm ${reqCls("color_base")}`}
                            testId="listing-color-base"
                        />
                    </div>
                    {/* Feb-2026 (User-Wunsch) — „Nur Pflichtfelder": Farbe Hersteller +
                        Finish (kein roter Pflichtfeld-Rahmen) ausblenden. */}
                    {(!essentialOnly || orderMode) && (
                    <>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Farbe Hersteller</Label>
                        <Input value={form.color} onChange={(e) => update("color", e.target.value)} placeholder="z. B. Racing Red, Tornado Grey" className="mt-1.5 rounded-sm" data-testid="listing-color" />
                    </div>
                    {!orderMode && (
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Finish</Label>
                        <div className="mt-1.5 flex items-center gap-4 h-10 flex-wrap" data-testid="listing-color-finish">
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
                                <input type="radio" name="finish" checked={form.color_finish === "matt"} onChange={() => update("color_finish", form.color_finish === "matt" ? "" : "matt")} className="accent-[#111827]" data-testid="finish-matt" />
                                Matt
                            </label>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
                                <input type="radio" name="finish" checked={form.color_finish === "glaenzend"} onChange={() => update("color_finish", form.color_finish === "glaenzend" ? "" : "glaenzend")} className="accent-[#111827]" data-testid="finish-glaenzend" />
                                Glänzend
                            </label>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
                                <input type="radio" name="finish" checked={form.color_finish === "metallic"} onChange={() => update("color_finish", form.color_finish === "metallic" ? "" : "metallic")} className="accent-[#111827]" data-testid="finish-metallic" />
                                Metallik
                            </label>
                        </div>
                    </div>
                    )}
                    </>
                    )}
                </div>
                {/* Phase 3.91 — Unfall/Schaden-Block ans untere Ende der Fahrzeug-Stammdaten verschoben (siehe nach Motor & Antrieb) */}

                {/* Phase 3.69 — Motor-Felder aus „Motor & Schadstoff" ans Ende der Basics verschoben (User-Wunsch 18 May 2026) */}
                {isMoto && (
                    <>
                        <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Motor &amp; Antrieb</h3>
                        </div>
                        {/* Mai-2026 (User-Wunsch 21 May): Hubraum + Leistung (kW) + Leistung (PS) + Höchstgeschw. + Kraftstoff + Tankinhalt + Zylinder in einer Zeile.
                            Iteration 13: Tankinhalt zwischen Kraftstoff und Zylinder eingefügt (kein Pflichtfeld).
                            Feb-2026 (User-Wunsch 22 May): Höchstgeschw. (T, km/h) zwischen Leistung (PS) und Kraftstoff verschoben + (auto)-Hinweis am PS-Label entfernt. */}
                        <div className={`md:col-span-2 grid grid-cols-1 sm:grid-cols-2 ${essentialOnly ? "lg:grid-cols-3" : "lg:grid-cols-7"} gap-3`} data-testid="engine-row">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Hubraum ccm (P.1) <KiBadge field="displacement" /></Label>
                                <Input type="number" min="0" required value={form.displacement} onChange={(e) => update("displacement", e.target.value)} placeholder="z. B. 1200" className={`mt-1.5 rounded-sm font-mono-num ${reqCls("displacement")}`} data-testid="form-displacement" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Leistung kW (P.2) <KiBadge field="power_kw" /></Label>
                                <Input type="number" min="0" required value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} className={`mt-1.5 rounded-sm font-mono-num ${reqCls("power_kw")}`} data-testid="listing-power-kw" />
                            </div>
                            {(!essentialOnly || orderMode) && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Leistung (PS) <KiBadge field="power_hp" /></Label>
                                <Input type="number" min="0" value={form.power_hp} onChange={(e) => update("power_hp", e.target.value)} className="mt-1.5 rounded-sm bg-gray-50 font-mono-num" data-testid="listing-power-hp" />
                            </div>
                            )}
                            {!essentialOnly && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Km/h (T) <KiBadge field="max_speed_kmh" /></Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="500"
                                    value={form.max_speed_kmh ?? ""}
                                    onChange={(e) => update("max_speed_kmh", e.target.value)}
                                    placeholder="160"
                                    className="mt-1.5 rounded-sm font-mono-num"
                                    data-testid="form-max-speed"
                                />
                            </div>
                            )}
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Kraftstoff (P.3) <KiBadge field="fuel" /></Label>
                                <FrequentSelect
                                    storageKey="listing.fuel"
                                    value={form.fuel}
                                    onValueChange={(v) => update("fuel", v)}
                                    options={FUEL_OPTIONS}
                                    placeholder="Benzin"
                                    triggerClassName={`mt-1.5 rounded-sm ${reqCls("fuel")}`}
                                    testId="listing-fuel"
                                />
                            </div>
                            {!essentialOnly && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                                    Tankinhalt
                                    <KiBadge field="tank_capacity_l" />
                                </Label>
                                <div className="relative mt-1.5">
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={form.tank_capacity_l || ""}
                                        onChange={(e) => update("tank_capacity_l", e.target.value)}
                                        placeholder="z. B. 18"
                                        className="rounded-sm font-mono-num pr-8"
                                        data-testid="form-tank-capacity"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px] pointer-events-none">L</span>
                                </div>
                            </div>
                            )}
                            {(!essentialOnly || orderMode) && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Zylinder <KiBadge field="cylinder_count" /></Label>
                                <Select value={form.cylinder_count ? String(form.cylinder_count) : "any"} onValueChange={(v) => update("cylinder_count", v === "any" ? "" : v)}>
                                    <SelectTrigger className={`mt-1.5 rounded-sm ${reqCls("cylinder_count")}`} data-testid="form-cylinder-count"><SelectValue placeholder="– keine Angabe –" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="any">– keine Angabe –</SelectItem>
                                        {CYLINDER_OPTIONS.map((c) => <SelectItem key={c} value={String(c)}>{c} Zylinder</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            )}
                        </div>

                        {/* Feb-2026 (User-Wunsch 24 May Iter 26): Führerschein-
                            + Steuer-Karte wurde von hier UNTER „Hinweise zu
                            Service & Wartung" (ExtraTechnicalSection) verschoben. */}

                        {/* Jul-2026 (User-Wunsch) — Antrieb · Getriebe · Anzahl der Gänge · Schadstoffklasse · Umweltplakette in EINER Zeile. */}
                        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="drive-emission-row">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Antrieb <KiBadge field="drive_type" /></Label>
                            <FrequentSelect
                                storageKey="listing.drive_type"
                                value={form.drive_type}
                                onValueChange={(v) => update("drive_type", v)}
                                options={DRIVE_TYPES}
                                placeholder="Antriebsart wählen…"
                                triggerClassName={`mt-1.5 rounded-sm ${reqCls("drive_type")}`}
                                testId="listing-drive-type"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Getriebe * <KiBadge field="transmission" /></Label>
                            <FrequentSelect
                                storageKey="listing.transmission"
                                value={form.transmission}
                                onValueChange={(v) => update("transmission", v)}
                                options={TRANSMISSION_OPTIONS}
                                placeholder="Schaltgetriebe"
                                triggerClassName={`mt-1.5 rounded-sm ${reqCls("transmission")}`}
                                testId="listing-transmission"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Anzahl der Gänge <KiBadge field="gears" /></Label>
                            <Input
                                type="number"
                                min="0"
                                max="30"
                                value={form.gears ?? ""}
                                onChange={(e) => update("gears", e.target.value)}
                                placeholder="z. B. 6"
                                className="mt-1.5 rounded-sm font-mono-num"
                                data-testid="listing-gears"
                            />
                        </div>
                        {/* Feb-2026 (User-Wunsch) — „Nur Pflichtfelder": Schadstoffklasse +
                            Umweltplakette (kein roter Pflichtfeld-Rahmen) ausblenden. */}
                        {!essentialOnly && (
                        <>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Schadstoffklasse (14) <KiBadge field="emission_class" /></Label>
                            <FrequentSelect
                                storageKey="listing.emission_class"
                                value={form.emission_class || "any"}
                                onValueChange={(v) => update("emission_class", v === "any" ? "" : v)}
                                options={[{ value: "any", label: "– keine Angabe –" }, ...EMISSION_CLASSES.map((e) => ({ value: e, label: e }))]}
                                noFreqValues={["any"]}
                                placeholder="Euro-Norm wählen…"
                                triggerClassName="mt-1.5 rounded-sm"
                                testId="listing-emission-class"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Umweltplakette</Label>
                            <FrequentSelect
                                storageKey="listing.umweltplakette"
                                value={form.umweltplakette || "gruen"}
                                onValueChange={(v) => update("umweltplakette", v)}
                                options={UMWELTPLAKETTEN}
                                triggerClassName="mt-1.5 rounded-sm"
                                testId="form-umweltplakette"
                            />
                        </div>
                        {!form.emission_class && euroSuggestion && (
                            <div className="col-span-full text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-1.5 flex flex-wrap items-center gap-2" data-testid="euro-norm-suggestion">
                                <span>
                                    <b>Vorschlag Schadstoffklasse: {euroSuggestion}</b> (abgeleitet aus {form.first_registration_date ? "Erstzulassung" : "Baujahr"} — kann fehlerhaft sein!).
                                    Verbindlich steht die Euro-Norm in der Zulassungsbescheinigung Teil I <b>Feld 14</b>, im COC-Dokument oder beim Hersteller.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => update("emission_class", euroSuggestion)}
                                    className="text-[10px] font-bold uppercase tracking-wider bg-amber-600 text-white rounded-sm px-2 py-1 hover:bg-amber-700"
                                    data-testid="euro-norm-apply-btn"
                                >
                                    {euroSuggestion} übernehmen
                                </button>
                            </div>
                        )}
                        </>
                        )}
                        </div>
                    </>
                )}

                {/* Mai-2026 (User-Wunsch 21 May Iteration 8) — Zusatzinfos für E-Antrieb:
                    sichtbar wenn Kraftstoff == "Elektro" (auch unabhängig von isMoto). */}
                {form.fuel === "Elektro" && (
                    <div className="md:col-span-2 mt-1 rounded-sm border border-gray-200 bg-gray-50 p-3 space-y-3" data-testid="electric-extra-block">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" /> Zusatzinformationen E-Antrieb
                            </div>
                            {/* Aug-2026 (User-Wunsch) — fehlende E-Daten per KI im Netz recherchieren */}
                            <button
                                type="button"
                                onClick={researchElectric}
                                disabled={eResearching || !form.brand || !form.model}
                                title={!form.brand || !form.model ? "Erst Marke + Modell erfassen" : "Fehlende Daten per KI im Netz recherchieren"}
                                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-white rounded-sm px-2.5 py-1.5 hover:bg-black disabled:opacity-40"
                                data-testid="electric-ai-research-btn"
                            >
                                {eResearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                Fehlende Daten per KI recherchieren
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Leistung (kW)</Label>
                                <Input type="number" min="0" value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} placeholder="z. B. 11" className="mt-1.5 rounded-sm font-mono-num" data-testid="electric-power-kw" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Reichweite (km)</Label>
                                <Input type="number" min="0" value={form.electric_range_km || ""} onChange={(e) => { update("electric_range_km", e.target.value); update("range_km", e.target.value); }} placeholder="z. B. 120" className="mt-1.5 rounded-sm font-mono-num" data-testid="electric-range-km" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ladedauer (Stunden)</Label>
                                <Input type="number" step="0.1" min="0" max="200" value={form.charging_time_hours || ""} onChange={(e) => update("charging_time_hours", e.target.value)} placeholder="z. B. 4.5" className="mt-1.5 rounded-sm font-mono-num" data-testid="electric-charging-time" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Aktuelle Akkuleistung (%)</Label>
                                <Input type="number" min="0" max="100" value={form.electric_battery_health_pct || ""} onChange={(e) => update("electric_battery_health_pct", e.target.value === "" ? "" : Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))} placeholder="z. B. 92" className="mt-1.5 rounded-sm font-mono-num" data-testid="electric-battery-health" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Akku</Label>
                                <div className="flex flex-wrap gap-2">
                                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="electric-battery-fixed-wrap">
                                        <input
                                            type="checkbox"
                                            checked={form.electric_battery_type === "fest"}
                                            onChange={(e) => { update("electric_battery_type", e.target.checked ? "fest" : ""); update("battery_type", e.target.checked ? "fixed" : ""); }}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid="electric-battery-fixed"
                                        />
                                        <span className="font-medium">Akku fest verbaut</span>
                                    </label>
                                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="electric-battery-removable-wrap">
                                        <input
                                            type="checkbox"
                                            checked={form.electric_battery_type === "herausnehmbar"}
                                            onChange={(e) => { update("electric_battery_type", e.target.checked ? "herausnehmbar" : ""); update("battery_type", e.target.checked ? "removable" : ""); }}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid="electric-battery-removable"
                                        />
                                        <span className="font-medium">Akku herausnehmbar</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Zertifikat</Label>
                                <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="electric-battery-cert-wrap">
                                    <input
                                        type="checkbox"
                                        checked={!!form.electric_battery_certificate}
                                        onChange={(e) => { update("electric_battery_certificate", e.target.checked); update("battery_certificate", e.target.checked); }}
                                        className="w-4 h-4 accent-[#111827]"
                                        data-testid="electric-battery-cert"
                                    />
                                    <span className="font-medium">Batteriezertifikat vorhanden</span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mai-2026 (User-Wunsch 21 May Iteration 8) — Zusatzinfos für Quad:
                    sichtbar wenn Fahrzeugtyp == "Quad" (nur bei Motorrad-Kategorie). */}
                {isMoto && form.vehicle_type === "Quad" && (
                    <div className="md:col-span-2 mt-1 rounded-sm border border-gray-200 bg-gray-50 p-3" data-testid="quad-extra-block">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Zusatzinformationen Quad</div>
                        <div className="flex flex-wrap gap-2">
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="quad-allwheel-wrap">
                                <input
                                    type="checkbox"
                                    checked={!!form.quad_allwheel_drive}
                                    onChange={(e) => update("quad_allwheel_drive", e.target.checked)}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid="quad-allwheel"
                                />
                                <span className="font-medium">Allrad</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="quad-lof-wrap">
                                <input
                                    type="checkbox"
                                    checked={!!form.quad_lof_zulassung}
                                    onChange={(e) => update("quad_lof_zulassung", e.target.checked)}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid="quad-lof"
                                />
                                <span className="font-medium">LOF-Zulassung</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="quad-towbar-wrap">
                                <input
                                    type="checkbox"
                                    checked={!!form.quad_towbar}
                                    onChange={(e) => update("quad_towbar", e.target.checked)}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid="quad-towbar"
                                />
                                <span className="font-medium">Anhängerkupplung</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827]" data-testid="quad-winch-wrap">
                                <input
                                    type="checkbox"
                                    checked={!!form.quad_winch}
                                    onChange={(e) => update("quad_winch", e.target.checked)}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid="quad-winch"
                                />
                                <span className="font-medium">Seilwinde</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Feb-2026 (User-Wunsch 22 May): Maße + Gewichte zwischen Schadstoffklasse
                    und Bemerkungen — aus dem ZB1-Extended-Block hochgezogen, damit
                    diese Werte beim ZB1-Scan direkt sichtbar in der Stammdaten-Sektion landen. */}
                {/* Feb-2026 (User-Wunsch UX): Maße & Gewichte sind optional → im
                    Essential-Only-Modus ausblenden */}
                {isMoto && !essentialOnly && (
                <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-5 gap-3 mt-1" data-testid="dimensions-weights-row">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Länge mm (18) <KiBadge field="length_mm" /></Label>
                        <Input type="number" min="0" value={form.length_mm ?? ""} onChange={(e) => update("length_mm", e.target.value)} placeholder="2150" className="mt-1.5 rounded-sm font-mono-num" data-testid="form-length" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Breite mm (19) <KiBadge field="width_mm" /></Label>
                        <Input type="number" min="0" value={form.width_mm ?? ""} onChange={(e) => update("width_mm", e.target.value)} placeholder="800" className="mt-1.5 rounded-sm font-mono-num" data-testid="form-width" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Höhe mm (20) <KiBadge field="height_mm" /></Label>
                        <Input type="number" min="0" value={form.height_mm ?? ""} onChange={(e) => update("height_mm", e.target.value)} placeholder="1100" className="mt-1.5 rounded-sm font-mono-num" data-testid="form-height" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Leergewicht kg (G) <KiBadge field="weight_kg" /></Label>
                        <Input type="number" min="0" value={form.weight_kg ?? ""} onChange={(e) => update("weight_kg", e.target.value)} placeholder="210" className="mt-1.5 rounded-sm font-mono-num" data-testid="form-weight" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zul. Gesamtg. kg (F.1)</Label>
                        <Input type="number" min="0" value={form.max_weight_kg ?? ""} onChange={(e) => update("max_weight_kg", e.target.value)} placeholder="460" className="mt-1.5 rounded-sm font-mono-num" data-testid="form-max-weight" />
                    </div>
                </div>
                )}

                {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v7) — Reifengrößen
                    aus ZB1 Pos. 15.1 (vorne) + 15.2 (hinten). Vorne/hinten kann
                    bei Motorrädern unterschiedlich sein, deswegen ZWEI Felder.
                    Iter 28-2 (User-Wunsch Feb-2026): Sitzhöhe + Tankinhalt wurden
                    hier ENTFERNT (Duplikat) — sie sind bereits oben in der
                    Sitzplätze-Reihe (Sitzhöhe) bzw. der Motor-&-Antrieb-Reihe
                    (Tankinhalt) vorhanden. */}
                {isMoto && !essentialOnly && (
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1" data-testid="tire-sizes-row">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Reifen vorne (15.1) <KiBadge field="tire_size_front" /></Label>
                        <Input
                            value={form.tire_size_front ?? ""}
                            onChange={(e) => update("tire_size_front", e.target.value)}
                            placeholder="z. B. 120/70 R15 56P"
                            className="mt-1.5 rounded-sm font-mono-num"
                            maxLength={40}
                            data-testid="form-tire-front"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Reifen hinten (15.2) <KiBadge field="tire_size_rear" /></Label>
                        <Input
                            value={form.tire_size_rear ?? ""}
                            onChange={(e) => update("tire_size_rear", e.target.value)}
                            placeholder="z. B. 130/70 R12 62P"
                            className="mt-1.5 rounded-sm font-mono-num"
                            maxLength={40}
                            data-testid="form-tire-rear"
                        />
                    </div>
                    {/* Jul-2026 (User-Wunsch) — Bemerkungen & Ausnahmen (22) in derselben Zeile wie die Reifengrößen. */}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Bemerkungen (KI KBA Daten) &amp; Ausnahmen (22) <KiBadge field="bemerkungen" /></Label>
                        <textarea
                            value={form.bemerkungen ?? ""}
                            onChange={(e) => update("bemerkungen", e.target.value)}
                            rows={2}
                            className="mt-1.5 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
                            placeholder="Auflagen, Beschränkungen, Anmerkungen aus ZB1 Feld 22"
                            data-testid="form-bemerkungen"
                        />
                    </div>
                </div>
                )}

                {/* Nicht-Motorräder: Bemerkungen & Ausnahmen (22) als eigene volle Zeile. */}
                {!isMoto && !essentialOnly && (
                <div className="md:col-span-2" data-testid="bemerkungen-row">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Bemerkungen (KI KBA Daten) &amp; Ausnahmen (22) <KiBadge field="bemerkungen" /></Label>
                    <textarea
                        value={form.bemerkungen ?? ""}
                        onChange={(e) => update("bemerkungen", e.target.value)}
                        rows={2}
                        className="mt-1.5 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
                        placeholder="Auflagen, Beschränkungen, Anmerkungen aus ZB1 Feld 22"
                        data-testid="form-bemerkungen"
                    />
                </div>
                )}

                {/* Phase 3.91 — Unfall/Schaden-Block ans untere Ende der Fahrzeug-Stammdaten verschoben.
                    Mai-2026 (User-Wunsch 21 May Iteration 8): KEIN Pflichtfeld mehr — Markierung entfernt (kein *, kein roter Rahmen).
                    Feb-2026 (User-Wunsch): im „Nur Pflichtfelder"-Modus ausgeblendet (kein roter Pflichtfeld-Rahmen). */}
                {!essentialOnly && (
                <div className="md:col-span-2 mt-1 rounded-sm p-3 space-y-3 border border-gray-200 bg-gray-50" data-testid="damage-status-block">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Unfall / Schaden</div>

                    {/* Mai-2026 — Unfallfrei + Unfallfahrzeug + UM-Kippschäden in einer Zeile (3-Spalten-Grid).
                        Iteration 11: gegenseitige Sperre (Unfallfrei XOR (Unfallfahrzeug | Kippschäden)). */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        {(() => {
                            const damaged = !!form.is_accident_vehicle || !!form.has_tipover_damage;
                            const isFree = !!form.accident_free;
                            const freeLockedByDamage = damaged && !isFree;
                            const damageLockedByFree = isFree;
                            return (
                                <>
                                    <label
                                        className={`inline-flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 ${freeLockedByDamage ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-[#111827]"}`}
                                        data-testid="extra-accident-free-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isFree}
                                            disabled={freeLockedByDamage}
                                            onChange={(e) => update("accident_free", e.target.checked)}
                                            className="w-4 h-4 accent-[#111827] disabled:cursor-not-allowed"
                                            data-testid="extra-accident-free"
                                        />
                                        <span className="font-medium text-[#111827]">Unfallfrei</span>
                                    </label>
                                    <label
                                        className={`inline-flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 ${damageLockedByFree ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-[#111827]"}`}
                                        data-testid="extra-accident-vehicle-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!form.is_accident_vehicle}
                                            disabled={damageLockedByFree}
                                            onChange={(e) => update("is_accident_vehicle", e.target.checked)}
                                            className="w-4 h-4 accent-[#111827] disabled:cursor-not-allowed"
                                            data-testid="extra-accident-vehicle"
                                        />
                                        <span className="font-medium text-[#111827]">Unfallfahrzeug</span>
                                    </label>
                                    <label
                                        className={`inline-flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 ${damageLockedByFree ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-[#111827]"}`}
                                        data-testid="extra-tip-damage-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!form.has_tipover_damage}
                                            disabled={damageLockedByFree}
                                            onChange={(e) => update("has_tipover_damage", e.target.checked)}
                                            className="w-4 h-4 accent-[#111827] disabled:cursor-not-allowed"
                                            data-testid="extra-tip-damage"
                                        />
                                        <span className="font-medium">UM-Kippschäden</span>
                                    </label>
                                    {/* Jul-2026 (User-Wunsch) — „Mängelhinweis": rechts neben Kippschäden,
                                        IMMER anklickbar (unabhängig von Unfallfrei), öffnet eigenes Eingabefeld. */}
                                    <label
                                        className="inline-flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-sm px-3 py-2 cursor-pointer hover:border-[#111827]"
                                        data-testid="extra-defect-note-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!form.has_defect_note}
                                            onChange={(e) => update("has_defect_note", e.target.checked)}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid="extra-defect-note"
                                        />
                                        <span className="font-medium">Mängelhinweis</span>
                                    </label>
                                </>
                            );
                        })()}
                    </div>
                    {/* Jul-2026 (User-Wunsch) — Freitext Mängelhinweis, sichtbar bei aktivem Mängelhinweis. */}
                    {form.has_defect_note && (
                    <div data-testid="defect-note-block">
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500" htmlFor="defect-note-text">
                            Mängelhinweis (optional)
                        </Label>
                        <textarea
                            id="defect-note-text"
                            value={form.defect_note || ""}
                            onChange={(e) => update("defect_note", e.target.value)}
                            rows={3}
                            maxLength={2000}
                            placeholder="z. B. Batterie schwach, Reifen hinten abgefahren, Blinker links defekt …"
                            className="mt-1 w-full border border-gray-300 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
                            data-testid="form-defect-note"
                        />
                        <p className="text-[10px] text-gray-500 mt-0.5">{(form.defect_note || "").length} / 2000 Zeichen</p>
                    </div>
                    )}

                    {/* Freitext: Schadensinformationen — Iteration 11: nur sichtbar, wenn Unfallfahrzeug oder Kippschäden angehakt. */}
                    {(form.is_accident_vehicle || form.has_tipover_damage) && (
                    <div data-testid="damage-info-block">
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500" htmlFor="damage-info-text">
                            Schadensinformationen (optional)
                        </Label>
                        <textarea
                            id="damage-info-text"
                            value={form.damage_info || ""}
                            onChange={(e) => update("damage_info", e.target.value)}
                            rows={3}
                            maxLength={2000}
                            placeholder="z. B. leichter Sturzschaden am Tank rechts (Lackkratzer ca. 5 cm), keine Rahmen­schäden, Reparatur durch Fachbetrieb dokumentiert…"
                            className="mt-1 w-full border border-gray-300 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
                            data-testid="form-damage-info"
                        />
                        <p className="text-[10px] text-gray-500 mt-0.5">{(form.damage_info || "").length} / 2000 Zeichen</p>
                    </div>
                    )}
                </div>
                )}

                {/* Feb-2026 (User-Wunsch): HSN / TSN / Fahrzeugklasse /
                    Kennzeichen direkt unter Unfall/Schaden — aus dem
                    ZB1-erweitert-Block hochgezogen, damit diese Kerndaten
                    sofort sichtbar sind und nicht im Akkordeon versteckt. */}
                {isMoto && !essentialOnly && (
                <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1 items-end" data-testid="hsn-tsn-row">
                    <div className="col-span-full flex justify-end">
                        <button
                            type="button"
                            onClick={() => setHsnLookupOpen(true)}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:border-[#FFCC00]"
                            title="HSN/TSN in der internen KBA-Fahrzeugdatenbank nachschlagen"
                            data-testid="basics-hsn-tsn-lookup-btn"
                        >
                            <Search className="w-3 h-3" /> HSN / TSN nachschlagen
                        </button>
                    </div>
                    <HsnTsnLookupModal
                        open={hsnLookupOpen}
                        onOpenChange={setHsnLookupOpen}
                        onPick={(r) => {
                            update("hsn", r.hsn || "");
                            update("tsn", r.tsn || "");
                            if (r.fahrzeugklasse && !form.vehicle_class) update("vehicle_class", r.fahrzeugklasse);
                            if (r.hersteller && !form.brand) update("brand", r.hersteller);
                            if (r.bezeichnung && !form.model) update("model", r.bezeichnung);
                            toast.success("HSN/TSN übernommen");
                        }}
                    />
                    {kbaInfo && (
                        <div className="col-span-full" data-testid="kba-designation-banner">
                            {kbaInfo.not_found ? (
                                <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-1.5">
                                    HSN/TSN nicht in der KBA-Datenbank gefunden — bitte Eingabe prüfen.
                                </div>
                            ) : (
                                <div className="text-[11px] font-bold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5" data-testid="kba-designation-text">
                                    KBA: {[kbaInfo.hersteller, kbaInfo.bezeichnung,
                                        kbaInfo.ccm ? `${kbaInfo.ccm} cm³` : null,
                                        kbaInfo.kw ? `${kbaInfo.kw} kW` : null,
                                        kbaInfo.erstzulassung_ab ? `ab ${kbaInfo.erstzulassung_ab}` : null,
                                    ].filter(Boolean).join(" · ")}
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">HSN (2.1) <KiBadge field="hsn" /></Label>
                        <Input
                            value={form.hsn ?? ""}
                            maxLength={4}
                            onChange={(e) => update("hsn", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="0588"
                            className="mt-1.5 rounded-sm font-mono-num"
                            data-testid="form-hsn"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">TSN (2.2) <KiBadge field="tsn" /></Label>
                        <div className="mt-1.5 grid grid-cols-[80px_1fr] gap-1.5" data-testid="tsn-split-wrap">
                            <Input
                                value={(form.tsn ?? "").slice(0, 3)}
                                maxLength={3}
                                onChange={(e) => {
                                    const head = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);
                                    const tail = (form.tsn ?? "").slice(3);
                                    update("tsn", head + tail);
                                }}
                                placeholder="ABC"
                                className="rounded-sm font-mono-num uppercase tracking-widest text-center"
                                data-testid="form-tsn-head"
                            />
                            <Input
                                value={(form.tsn ?? "").slice(3)}
                                maxLength={9}
                                onChange={(e) => {
                                    const tail = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
                                    const head = (form.tsn ?? "").slice(0, 3);
                                    update("tsn", head + tail);
                                }}
                                placeholder="000062"
                                className="rounded-sm font-mono-num"
                                data-testid="form-tsn-tail"
                            />
                        </div>
                        {/* Hidden mirror for backwards-compat test harnesses */}
                        <input type="hidden" value={form.tsn ?? ""} data-testid="form-tsn" readOnly />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Fahrzeugklasse (J) <KiBadge field="vehicle_class" /></Label>
                        <Input
                            value={form.vehicle_class ?? ""}
                            onChange={(e) => update("vehicle_class", e.target.value)}
                            placeholder="L3e-A1 / L5e-A / M1"
                            className="mt-1.5 rounded-sm"
                            data-testid="form-vehicle-class"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">Kennzeichen (A) <KiBadge field="license_plate" /></Label>
                        <Input
                            value={form.license_plate ?? ""}
                            onChange={(e) => update("license_plate", e.target.value.toUpperCase())}
                            placeholder="M-AB 1234"
                            className="mt-1.5 rounded-sm font-mono-num"
                            data-testid="form-license-plate"
                        />
                    </div>
                </div>
                )}

                {/* Mai-2026 — ZB1 / COC erweiterte Stammdaten direkt unter Unfall/Schaden (User-Wunsch).
                    Feb-2026 (User-Wunsch 24 May Iter 19): Vorbesitzer-Block in
                    „Disposition / Portal-Transaktion" verschoben — gehört
                    fachlich zur Vor-Eigentümer-Historie, nicht zu Fahrzeug-
                    Stammdaten. Hier nicht mehr rendern. */}

                {/* Phase 3.73 — MwSt / Differenzbesteuerung — Feb-2026 (User-Wunsch Iter 28):
                    MwSt-Block + Export-Optionen wurden in `PriceFieldsBlock` verschoben
                    und sitzen jetzt direkt OBERHALB des B2C-Preises (fachliche Nähe
                    zwischen Steuerregime und Preisangabe). Hier nicht mehr rendern. */}

                {/* Feb-2026 (User-Wunsch 24 May Iter 27) — Preise B2C/B2B/Vermittlungs-
                    provision sowie KI-Preisvorschlag wurden in die neue Komponente
                    `PriceFieldsBlock` extrahiert und unterhalb der „Bilder & Video"-
                    Sektion in FooterActionsSection eingebettet. */}
            </div>
            </SectionToggle>
        </>
    );
}
